import React, { useEffect, useMemo, useState } from "react";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import { Switch } from "@mui/material";
import {
  CmpInternalEmailKeyEnum,
  CmpInternalEmailSettingFieldsFragment,
  useGetChamberQuery,
  useGetCmpInternalEmailSettingsQuery,
  useUpdateCmpInternalEmailSettingMutation,
} from "../../../../../graphql/types";
import { getPreserveExpectedErrorsForCallerContext } from "../../../../../utils/apolloErrorPolicy";
import { applyCmpOrganizationNoun } from "../../../../../utils/cmpOrganizationNoun";
import { useCmpOrganizationNoun } from "../../../../../contexts/CmpOrganizationNounContext";
import { getUserFacingErrorText, STAFF_SUPPORT_EMAIL } from "../../../../../utils/userFacingError";
import { ActionSuccessDialog } from "../../../../common/dashboard/ActionSuccessDialog";
import { modelOffRedSwitchSx, SectionSaveReset, SettingsModal } from "../common";
import { ChamberSettingsFormState } from "../settingsData";
import { emailCardFieldClass, emailCardInputClass, emailCardPrimaryButton, StepNumber } from "./emailCardParts";
import { formatAutomatedEmailTime } from "./emailTabTime";
import { getProspectLeadEmailView, INTERNAL_EMAIL_SENDER, InternalEmail, internalEmails, PROSPECT_LEAD_TEMPLATE_ID } from "./internalEmailCatalogue";
import { getInternalEmailSendToError, parseInternalEmailSendTo } from "./internalEmailSendTo";

const internalEmailErrorContext = getPreserveExpectedErrorsForCallerContext({ expectedGraphQLErrorCodes: ["BAD_USER_INPUT"] });
const readOnlyInputClass = `${emailCardInputClass} !cursor-not-allowed !bg-[#F1F1F1] lg:self-start`;
const VIEW_ONLY_NOTE = "View only · This email's content can't be edited";
const NOT_SAVED_TEXT = `We could not save this email. Your changes are still here. Select Save Changes again. If it keeps happening, email ${STAFF_SUPPORT_EMAIL}.`;
export const INTERNAL_EMAIL_SAVED_MESSAGE = "Your changes have been saved successfully!";
export const INTERNAL_EMAIL_NO_CHANGES_MESSAGE = "This email has no changes to save.";

type ViewedEmail = { title: string; subject: string; body: string; note: string };
type Saved = CmpInternalEmailSettingFieldsFragment;
type Draft = { enabled: boolean; sendTo: string };

const toDraft = (saved: Saved): Draft => ({ enabled: saved.enabled, sendTo: saved.sendTo.join(", ") });
const isDraftDirty = (draft: Draft, saved: Saved) =>
  draft.enabled !== saved.enabled || parseInternalEmailSendTo(draft.sendTo).join(",") !== saved.sendTo.join(",");

/**
 * Monday 13225040388 (N4b) and 13362632215 (Moeen, Nov 1 2026): design (25)
 * "Internal Emails Sent from Chamber Member Pro™" (CMP Dashboard.dc.html:9473,
 * data :17161). Each card has an ENABLED/DISABLED switch, an editable Send To
 * (Chamber Staff Email) and its own Reset, Save Changes and Last Saved.
 * DISABLED = the chamber gets no internal copy. ENABLED needs a Send To.
 * Sent From stays info@chambermemberpro.com (IF13 Q-D).
 */
const InternalEmailsSection = ({ chamberId, savedEmail, timeZone, onDirtyChange, onSaved }: {
  chamberId: string;
  savedEmail: ChamberSettingsFormState["email"];
  timeZone?: string;
  onDirtyChange?: (dirty: boolean) => void;
  onSaved?: () => void;
}) => {
  const noun = useCmpOrganizationNoun();
  const { data: chamberData } = useGetChamberQuery({ variables: { chamberId }, skip: !chamberId, context: internalEmailErrorContext });
  const { data, loading, error, refetch } = useGetCmpInternalEmailSettingsQuery({
    variables: { chamberId }, skip: !chamberId, fetchPolicy: "network-only", context: internalEmailErrorContext,
  });
  const [updateSetting] = useUpdateCmpInternalEmailSettingMutation({ context: internalEmailErrorContext });
  const [savedByKey, setSavedByKey] = useState<Partial<Record<CmpInternalEmailKeyEnum, Saved>>>({});
  const [drafts, setDrafts] = useState<Partial<Record<CmpInternalEmailKeyEnum, Draft>>>({});
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<CmpInternalEmailKeyEnum, string>>>({});
  const [saveErrors, setSaveErrors] = useState<Partial<Record<CmpInternalEmailKeyEnum, string>>>({});
  const [savingKey, setSavingKey] = useState<CmpInternalEmailKeyEnum | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [viewed, setViewed] = useState<ViewedEmail | null>(null);

  useEffect(() => {
    const loaded = data?.getCmpInternalEmailSettings;
    if (!loaded) return;
    setSavedByKey(Object.fromEntries(loaded.map(setting => [setting.key, setting])));
    setDrafts(Object.fromEntries(loaded.map(setting => [setting.key, toDraft(setting)])));
    setFieldErrors({});
    setSaveErrors({});
  }, [data]);

  const dirty = useMemo(() => internalEmails.some(email => {
    const saved = savedByKey[email.apiKey];
    const draft = drafts[email.apiKey];
    return !!saved && !!draft && isDraftDirty(draft, saved);
  }), [drafts, savedByKey]);
  useEffect(() => { onDirtyChange?.(dirty); }, [dirty, onDirtyChange]);
  useEffect(() => () => onDirtyChange?.(false), [onDirtyChange]);

  const prospectTemplate = savedEmail.emailTemplates.find(template => template.id === PROSPECT_LEAD_TEMPLATE_ID);
  const prospectView = getProspectLeadEmailView(prospectTemplate);
  const retry = async () => {
    try { await refetch(); }
    catch { /* The query displays the error, and Apollo reports unexpected failures. */ }
  };

  const setDraft = (key: CmpInternalEmailKeyEnum, patch: Partial<Draft>) => {
    setDrafts(current => ({ ...current, [key]: { ...(current[key] as Draft), ...patch } }));
    setFieldErrors(current => ({ ...current, [key]: undefined }));
    setSaveErrors(current => ({ ...current, [key]: undefined }));
  };

  const save = async (email: InternalEmail) => {
    const saved = savedByKey[email.apiKey];
    const draft = drafts[email.apiKey];
    if (!saved || !draft || savingKey) return;
    const fieldError = getInternalEmailSendToError({ enabled: draft.enabled, key: email.apiKey, sendTo: draft.sendTo });
    if (fieldError) {
      setFieldErrors(current => ({ ...current, [email.apiKey]: fieldError }));
      return;
    }
    if (saved.saved && !isDraftDirty(draft, saved)) {
      setSuccess(INTERNAL_EMAIL_NO_CHANGES_MESSAGE);
      return;
    }
    setSavingKey(email.apiKey);
    try {
      const result = await updateSetting({
        variables: { input: {
          chamberId,
          key: email.apiKey,
          enabled: draft.enabled,
          sendTo: parseInternalEmailSendTo(draft.sendTo),
          expectedUpdatedAtUtcMilli: saved.updatedAtUtcMilli ?? null,
        } },
      });
      const next = result.data?.updateCmpInternalEmailSetting;
      if (!next) throw new Error("No result");
      setSavedByKey(current => ({ ...current, [email.apiKey]: next }));
      setDrafts(current => ({ ...current, [email.apiKey]: toDraft(next) }));
      setSuccess(INTERNAL_EMAIL_SAVED_MESSAGE);
      onSaved?.();
    } catch (saveError) {
      setSaveErrors(current => ({ ...current, [email.apiKey]: getUserFacingErrorText(saveError, NOT_SAVED_TEXT) }));
    } finally {
      setSavingKey(null);
    }
  };

  return (
    <section className="flex flex-col gap-[16px] text-[#0A0A0A]">
      <div className="flex flex-col gap-[4px] border-t-2 border-[#0A0A0A] pt-[16px]">
        <h2 className="text-[18px] font-semibold">Internal Emails Sent from Chamber Member Pro™ to {chamberData?.getChamber?.name || applyCmpOrganizationNoun("your chamber", noun)}</h2>
        <p className="text-[14px] leading-[1.55]">Enter the staff email address you would like to receive a copy of this automated email. All emails will come from {INTERNAL_EMAIL_SENDER}.</p>
      </div>
      {error && <div role="alert" className="text-[14px] text-red-600">{applyCmpOrganizationNoun(`We could not load your chamber's staff emails. Select Try again. If it keeps happening, email ${STAFF_SUPPORT_EMAIL}.`, noun)} <button type="button" className="underline" onClick={retry}>Try again</button></div>}
      <div className="flex flex-col gap-[16px]">
        {internalEmails.map((email, index) => {
          const content = email.key === "prospectLead" ? prospectView : { subject: email.subject, body: email.body };
          const note = email.editNote ? `View only here · ${email.editNote}` : VIEW_ONLY_NOTE;
          const saved = savedByKey[email.apiKey];
          const draft = drafts[email.apiKey];
          const enabled = draft ? draft.enabled : true;
          const fieldError = fieldErrors[email.apiKey];
          const saving = savingKey === email.apiKey;
          const fieldId = `internal-email-send-to-${email.key}`;
          return (
            <article key={email.key} data-testid={`internal-email-${email.key}`} className="flex min-w-0 flex-col gap-[22px] rounded-[12px] border border-[#E3E3E6] bg-white p-[20px] shadow-sm sm:p-[28px]">
              <div className="flex items-start justify-between gap-[20px] border-b border-[#ECECEC] pb-[16px]">
                <div className="min-w-0">
                  <h3 className="text-[16px] font-bold leading-[1.4]">{index + 1}. {email.title} <span className="font-semibold">(Internal Email Only)</span></h3>
                  <p className="mt-[5px] text-[14px] leading-[1.55]"><strong className="font-semibold">{applyCmpOrganizationNoun("Sent to your chamber staff:", noun)}</strong> {applyCmpOrganizationNoun(email.description, noun)}</p>
                </div>
                <div className="flex shrink-0 items-center gap-[10px]">
                  <Switch
                    checked={enabled}
                    disabled={!draft || saving}
                    onChange={event => setDraft(email.apiKey, { enabled: event.target.checked })}
                    sx={modelOffRedSwitchSx}
                    inputProps={{ "aria-label": `${email.title} (Internal Email Only)` }}
                  />
                  <span className={`text-[12px] font-semibold tracking-[.04em] ${enabled ? "text-[#10B981]" : "text-[#E24C4C]"}`}>{enabled ? "ENABLED" : "DISABLED"}</span>
                </div>
              </div>
              <div className="flex flex-col gap-[10px]">
                <div className="flex items-center gap-[10px]"><StepNumber number={1} /><span className="text-[14px] font-semibold">Review the email</span></div>
                <div className="flex flex-wrap items-center justify-between gap-[20px] rounded-[10px] border border-[#ECECEC] bg-[#FCFCFD] px-[20px] py-[16px]">
                  <div className="flex min-w-0 flex-[1_1_320px] items-start gap-[12px]"><EmailOutlinedIcon sx={{ fontSize: 18, marginTop: "3px" }} /><div className="min-w-0"><p className="text-[12px] font-bold tracking-[.06em]">EMAIL SUBJECT</p><p title={content?.subject} className="mt-[3px] truncate text-[14px] font-semibold">{content ? content.subject : "We could not load this email. Refresh the page."}</p><p className="mt-[3px] text-[12px]">{note}</p></div></div>
                  <button type="button" disabled={!content} onClick={() => content && setViewed({ title: email.title, subject: content.subject, body: content.body, note: email.editNote || "The content of internal emails can't be edited." })} className={`${emailCardPrimaryButton} shrink-0 !py-[11px] !text-[13px]`}>View Email</button>
                </div>
              </div>
              <div className="grid min-w-0 grid-cols-1 gap-[20px] lg:grid-cols-2">
                <label className={emailCardFieldClass}><span className="flex items-center gap-[10px] text-[14px] font-semibold"><StepNumber number={2} />Automated (Sent From) Email</span><span className="text-[13px] leading-[1.5]">This email is automatically sent by Chamber Member Pro™ and cannot be changed.</span><input type="email" readOnly aria-readonly="true" className={readOnlyInputClass} value={INTERNAL_EMAIL_SENDER} title={INTERNAL_EMAIL_SENDER} /></label>
                <div className={emailCardFieldClass}>
                  <label htmlFor={fieldId} className="flex items-center gap-[10px] text-[14px] font-semibold"><StepNumber number={3} />Send To (Chamber Staff Email)</label>
                  <span className="text-[13px] leading-[1.5]">Enter the staff email address you would like to receive a copy of this internal automated email.</span>
                  <div className="flex min-w-0 flex-col gap-[4px]">
                    <input
                      id={fieldId}
                      type="text"
                      className={`${emailCardInputClass} ${fieldError ? "!border-[#E24C4C]" : ""}`}
                      value={draft ? draft.sendTo : ""}
                      title={draft?.sendTo || undefined}
                      disabled={!draft || saving}
                      placeholder={!draft && loading ? "Loading staff emails..." : "Add the staff email to receive this"}
                      aria-invalid={!!fieldError}
                      aria-describedby={fieldError ? `${fieldId}-error` : undefined}
                      onChange={event => setDraft(email.apiKey, { sendTo: event.target.value })}
                    />
                    {fieldError && <p id={`${fieldId}-error`} role="alert" className="text-[13px] text-[#E24C4C]">{fieldError}</p>}
                  </div>
                </div>
              </div>
              <SectionSaveReset
                variant="design"
                lastSavedTone="strong"
                allowUnchangedSave
                allowUnchangedReset
                isDirty={!!saved && !!draft && isDraftDirty(draft, saved)}
                saving={saving}
                saveDisabled={!saved || !!savingKey}
                lastSavedLabel={saved?.updatedAtUtcMilli ? `Last Saved ${formatAutomatedEmailTime(saved.updatedAtUtcMilli, timeZone)}` : "Not saved yet"}
                onSave={() => save(email)}
                onReset={() => { if (saved) { setDrafts(current => ({ ...current, [email.apiKey]: toDraft(saved) })); setFieldErrors(current => ({ ...current, [email.apiKey]: undefined })); setSaveErrors(current => ({ ...current, [email.apiKey]: undefined })); } }}
                errorText={saveErrors[email.apiKey] || null}
              />
            </article>
          );
        })}
      </div>
      <ActionSuccessDialog message={success} onClose={() => setSuccess(null)} />
      {viewed && <InternalEmailViewDialog email={viewed} onClose={() => setViewed(null)} />}
    </section>
  );
};

// Design (24) "View Internal Automated Email" pop-up (CMP Dashboard.dc.html:5748).
const InternalEmailViewDialog = ({ email, onClose }: { email: ViewedEmail; onClose: () => void }) => (
  <SettingsModal variant="automatedEmail" description="" title="View Internal Automated Email" onClose={onClose} panelClassName="max-w-[900px] rounded-[14px] [font-family:DM_Sans,Arial,sans-serif]">
    <div className="flex flex-col gap-[16px] text-[#0A0A0A]">
      <h3 className="text-[15px] font-bold">{email.title} (Internal Email Only)</h3>
      <div className="flex flex-col gap-[10px] rounded-[10px] border border-[#ECECEC] bg-[#F7F7F8] px-[18px] py-[16px]">
        <p className="text-[13px] font-bold">Email Subject</p>
        <div title={email.subject} className="w-full break-words rounded-[8px] border border-[#9E9E9E] bg-white px-[13px] py-[11px] text-[14px]">{email.subject}</div>
      </div>
      <div className="flex flex-col gap-[10px] rounded-[10px] border border-[#ECECEC] bg-[#F7F7F8] px-[18px] py-[16px]">
        <p className="text-[13px] font-bold">Email Body</p>
        <div className="overflow-hidden rounded-[8px] border border-[#ECECEC] bg-white">
          <div data-testid="internal-email-view-body" className="flex max-h-[44vh] min-h-[240px] flex-col gap-[10px] overflow-y-auto break-words p-[16px] text-[12px] leading-[1.6]">
            {email.body.split(/\n\s*\n/).map((paragraph, paragraphIndex) => (
              <div key={paragraphIndex}>{paragraph.split("\n").map((line, lineIndex) => (
                <div key={lineIndex} className="min-h-[1.6em]">{line.split("**").map((segment, segmentIndex) => segment && <span key={segmentIndex} className={segmentIndex % 2 ? "font-bold" : undefined}>{segment}</span>)}</div>
              ))}</div>
            ))}
          </div>
        </div>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-[12px]">
        <span className="text-[13px]">{email.note}</span>
        <button type="button" onClick={onClose} className="rounded-[8px] bg-[#6E6E6E] px-[34px] py-[12px] text-[14px] font-semibold text-white">Close</button>
      </div>
    </div>
  </SettingsModal>
);

export default InternalEmailsSection;
